import { type Ref, useEffectEvent, useLayoutEffect } from "react";
import ShopWindowHeader from "@/components/shopwindow/ShopWindowHeader";
import RecipeSummary from "@/components/shopwindow/RecipeSummary";
import MethodSwiper from "@/components/shopwindow/MethodSwiper";
import ShopWindowFooter from "@/components/shopwindow/ShopWindowFooter";
import UpNextList from "@/components/shopwindow/UpNextList";
import NextVideoBlock from "@/components/shopwindow/NextVideoBlock";
import { linkTypes } from "@/plugin/LinkData";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import { shopWindowTypes, eventNames } from "@/modules/Constants";
import {
  isChainActive,
  isChainAnchoredOnMethodStep,
} from "@/modules/chainReveal";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import { isMcpLandscapeLayout } from "@/modules/mcpSurface";
import type { ExperienceState } from "@/types/state";

const REGION = "Method Shop Window";

interface CtaLink {
  type?: string;
  label?: { tap?: { title?: string } };
}

type MethodShopWindowProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  hasHeader?: boolean;
  hasExitButton?: boolean;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

export default function MethodShopWindow({
  experienceState,
  config,
  hasHeader,
  hasExitButton,
  nodeRef,
  onApplicationEvent,
}: MethodShopWindowProps) {
  const onDidMount = useEffectEvent(() => {
    onApplicationEvent({
      region: REGION,
      name: eventNames.SHOP_WINDOW_DID_MOUNT,
      type: shopWindowTypes.METHOD,
    });
  });
  // componentDidMount: layout phase, after the children's mount effects.
  useLayoutEffect(() => {
    onDidMount();
  }, []);

  const onExit = () => {
    if (onApplicationEvent) {
      onApplicationEvent({
        name: eventNames.SHOP_WINDOW_EXIT,
        region: REGION,
        shopWindowConfig: config,
      });
    }
  };

  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;
  const ctaLink = (config.links ? config.links.cta : null) as CtaLink | null;
  const showHintTap = config.data.showHintTap;

  const header = hasHeader ? (
    <ShopWindowHeader
      src={experienceData.getLogoImageUrl()}
      hasExitButton={hasExitButton}
      onExit={onExit}
    />
  ) : null;

  let footer = null;

  if (ctaLink) {
    const clusterIndex = config.clusterArticleIndexAt(
      experienceData,
      config.index,
    );
    const article = experienceData.getArticle(
      clusterIndex.clusterName,
      clusterIndex.cindex,
      clusterIndex.index,
    );
    const isHidden = article == null;

    let isSelected: boolean;
    let isActive: boolean;

    if (ctaLink.type === linkTypes.ARTICLE) {
      isSelected = false;
      isActive = (article &&
        article.urls &&
        (article.urls as unknown[]).length > 0 &&
        !isSelected) as boolean;
    } else {
      isSelected = false;
      isActive = true;
    }

    footer = (
      <ShopWindowFooter
        experienceState={experienceState}
        config={config}
        isSelected={isSelected}
        isActive={isActive}
        isHidden={isHidden}
        pagination={config.data.showPagination}
        onApplicationEvent={onApplicationEvent}
      />
    );
  }

  let hintTap = null;

  if (showHintTap && applicationData.isPortrait()) {
    const clusterIndex = config.clusterArticleIndexAt(
      experienceData,
      config.index,
    );
    const label = clusterIndex.cta ? (clusterIndex.cta as CtaLink).label : null;
    const tapText = label?.tap ? label.tap.title : " ";
    hintTap = (
      <div className="hint-tap tw:[.portrait_.method-shop-window_&]:order-2 tw:[.portrait_.method-shop-window_&]:hidden tw:[.portrait_.shop-window_&]:[line-height:1]">
        <span>{tapText}</span>
      </div>
    );
  }

  const recipeSummary = applicationData.isLandscape() ? (
    <RecipeSummary hasDescription={false} experienceState={experienceState} />
  ) : null;

  // Desktop landscape (the split view) renders the chain inline in this shop
  // window. Mobile immersive (LandscapeChainPrompt) and MCP landscape (McpUpNext)
  // own the chain elsewhere, and in portrait it lives in the panel-level
  // `portrait-chain`. UpNextList and NextVideoBlock gate themselves on the chain
  // store, so they stay hidden until the reveal.
  const inlineLandscapeChain =
    applicationData.isLandscape() &&
    !isImmersiveLandscapeSize() &&
    !isMcpLandscapeLayout();
  // The `shop-window--chain-end` modifier switches to the chain-end layout:
  // last-step content and the Up Next chain share one scroll region. Portrait
  // anchors the step as soon as the Up Next prompt appears on the last step.
  const chainEnd = applicationData.isPortrait()
    ? isChainAnchoredOnMethodStep(applicationData, config, experienceData)
    : inlineLandscapeChain && isChainActive(applicationData);
  const rootClass =
    "method-shop-window" +
    (chainEnd ? " shop-window--chain-end" : "") +
    " tw:flex tw:flex-col tw:items-stretch tw:bg-[#fff]";

  return (
    <div className={rootClass} ref={nodeRef}>
      {header}
      {hintTap}
      {recipeSummary}
      <MethodSwiper
        experienceState={experienceState}
        config={config}
        onApplicationEvent={onApplicationEvent}
      />
      {inlineLandscapeChain ? (
        <UpNextList
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      ) : null}
      {footer}
      {inlineLandscapeChain ? (
        <NextVideoBlock
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      ) : null}
    </div>
  );
}
